import { createTheme } from '@material-ui/core/styles'
import { outlinedInputThemeOverrides } from 'lib/form-field-styles'
import { segmentedButtonThemeOverrides } from 'lib/segmented-button-styles'
import createBreakpoints from '@material-ui/core/styles/createBreakpoints'
import plusJakarta from 'fonts/plusJakarta'
import materialSymbolMuiDefaults from 'components/material-symbol/mui-defaults'
import '@fontsource/inter/300'
import '@fontsource/inter/400'
import '@fontsource/inter/500'
import '@fontsource/inter/600'
import '@fontsource/inter/700'
import '@fontsource-variable/material-symbols-rounded/full'
import '@fontsource/source-serif-pro/700.css'
import '@fontsource/nunito-sans/600.css'
import '@fontsource/nunito-sans/700.css'

const breakpoints = createBreakpoints({})

const fonts = {
  headline: 'Inter',
  eyebrow: 'Inter',
  text: 'Inter',
  form: 'Inter',
}

const headlineFont = [fonts.headline, 'sans-serif'].join(',')
const defaultFont = [fonts.text, 'sans-serif'].join(',')
const eyebrowFont = [fonts.eyebrow, 'sans-serif'].join(',')
const formFont = [fonts.form, 'sans-serif'].join(',')

export { headlineFont, defaultFont, eyebrowFont, formFont }

const headlineWeight = 700
const utilityWeight = 700
const bodyWeight = 300

const commonTheme = {
  palette: {
    primary: {
      main: '#0075CA',
      dark: '#004A99',
      light: '#5BA3FE',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#FF7500',
      light: '#ffa641',
      dark: '#c44500',
      contrastText: '#FFFFFF',
    },
    success: {
      main: '#4CAF50',
      light: '#7BC67E',
      dark: '#3B873E',
      contrastText: '#FFFFFF',
    },
    error: {
      light: '#e57373',
      main: '#f44336',
      dark: '#d32f2f',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.64)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    action: {
      hoverOpacity: 0.04,
      selectedOpacity: 0.08,
    },
    divider: '#E0E0E0',
    blue: {
      main: '#3268A7',
      light: '#EFF5FB',
      light25: '#F5F7FA',
      light100: '#EBEFF5',
      badge: '#2380FF',
      dark: '#001529',
      medium: '#EBEFF5',
    },
    // App shell sidebar tokens, mapped onto Material 3 color roles:
    //   itemText     => on-secondary-container
    //   activeBg     => secondary-container
    //   hoverOverlay => on-secondary-container @ 8% (M3 hover state layer)
    //   focusOverlay => on-secondary-container @ 12% (M3 focus state layer)
    sidebar: {
      itemText: '#172024',
      itemBorderRadius: 100,
      activeBg: '#EBF0F2',
      hoverOverlay: 'rgba(25, 28, 29, 0.08)',
      focusOverlay: 'rgba(25, 28, 29, 0.12)',
    },
  },
  typography: {
    fontFamily: ['Inter', 'Inter', 'Inter', 'Helvetica', 'Arial', 'sans-serif'],
    button: {
      textTransform: 'capitalize',
    },
    h4: {
      fontSize: '34px',
      letterSpacing: '0.25px',
    },
    h6: {
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: '32px',
      letterSpacing: '0.15px',
    },
    subtitle1: {
      fontSize: '16px',
      fontWeight: 500,
    },
  },
  props: {
    MuiTextField: {
      variant: 'outlined',
      fullWidth: true,
    },
    MuiFormControl: {
      variant: 'outlined',
      fullWidth: true,
    },
    ...materialSymbolMuiDefaults,
  },
  overrides: {
    ...outlinedInputThemeOverrides,
    ...segmentedButtonThemeOverrides,
    MuiTableRow: {
      root: {
        '&:last-child td': {
          borderBottom: 0,
        },
      },
    },
    MuiTableCell: {
      root: {
        borderBottomColor: '#EEEEEE',
        borderBottomStyle: 'solid',
        borderBottomWidth: 1,
      },
      head: {
        color: 'rgba(0, 0, 0, 0.38)',
        fontSize: 14,
      },
    },
    MuiDialogActions: {
      root: {
        padding: '16px',
        justifyContent: 'flex-end',
      },
    },
    MuiChip: {
      icon: {
        '&.MuiSvgIcon-root': {
          fontSize: 18,
          width: '1em',
          height: '1em',
        },
      },
      avatar: {
        fontSize: 16,
        '&.MuiSvgIcon-root': {
          fontSize: 'inherit',
          width: '1em',
          height: '1em',
        },
        '& .MuiSvgIcon-root': {
          fontSize: 'inherit',
          width: '1em',
          height: '1em',
        },
      },
      deleteIcon: {
        fontSize: 18,
        '&.MuiSvgIcon-root': {
          fontSize: 'inherit',
          width: '1em',
          height: '1em',
        },
      },
    },
    MuiAvatar: {
      root: {
        '& .MuiSvgIcon-root': {
          fontSize: 'inherit',
          width: '1em',
          height: '1em',
        },
      },
    },
    MuiButton: {
      root: {
        textTransform: 'capitalize',
        '& .MuiButton-label': {
          textTransform: 'capitalize',
        },
      },
      label: {
        textTransform: 'capitalize',
      },
    },
    ...outlinedInputThemeOverrides,
  },
}

// theme overrides for family cup UI
const familyCupTheme = {
  palette: {
    primary: {
      main: '#49C5B1',
      dark: '#38A48E',
      light: '#5BA3FE',
      contrastText: '#FFFFFF',
      selected: 'rgba(0, 117, 202, 0.08)',
    },
    secondary: {
      main: '#FF7500',
      light: '#ffa641',
      dark: '#c44500',
      contrastText: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: ['Inter', 'Inter', 'Inter', 'Helvetica', 'Arial', 'sans-serif'],
    button: {
      fontFamily: 'Inter',
      textTransform: 'capitalize',
      fontStyle: 'normal',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '21px',
    },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '@font-face': plusJakarta,
      },
    },
    MuiDrawer: {
      paper: {
        backgroundColor: '#49C5B1',
        color: '#FFFFFF',
      },
    },
    MuiListItemIcon: {
      root: {
        color: '#FFFFFF',
      },
    },
    MuiDialogActions: {
      root: {
        padding: '16px',
        justifyContent: 'flex-end',
      },
    },
  },
}

// Junior League theme
const juniorLeagueTheme = {
  palette: {
    primary: {
      main: '#0075CA',
      dark: '#004A99',
      light: '#5BA3FE',
      contrastText: '#FFFFFF',
      selected: 'rgba(0, 117, 202, 0.08)',
    },
    secondary: {
      main: '#FF7500',
      light: '#ffa641',
      dark: '#c44500',
      contrastText: '#FFFFFF',
    },
    card: {
      border: '#EEE',
      borderHover: '#FF7500',
      divider: '#DBDCDD',
      distance: {
        background: '#EEE',
      },
      status: {
        border: '#49C5B1',
        text: '#2C2D36',
        background: {
          main: '#49C5B1',
          dark: '#2C2D36',
        },
      },
    },
  },
  typography: {
    fontFamily: ['Inter', 'Inter', 'Inter', 'Helvetica', 'Arial', 'sans-serif'],
  },
  overrides: {
    MuiDrawer: {
      paper: {
        backgroundColor: '#0075CA',
        color: '#FFFFFF',
      },
    },
    MuiListItemIcon: {
      root: {
        color: '#FFFFFF',
      },
    },
    MuiDialogActions: {
      root: {
        padding: '16px',
        justifyContent: 'flex-end',
      },
    },
    MuiInputLabel: {
      outlined: {
        width: 'calc(100% - 28px)',
      },
    },
    MuiButtonBase: {
      root: {
        fontWeight: 400,
        '& .MuiButton-label': {
          fontWeight: 400,
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: '50px',
        boxShadow: 'none',
        textTransform: 'capitalize',
        fontWeight: 400,
        minWidth: 'auto',
        height: 40,
        minHeight: 40,
        padding: '8px 16px',
        fontSize: '0.875rem',
        lineHeight: 1.5,
        '& .MuiButton-label': {
          fontWeight: 400,
          textTransform: 'capitalize',
          width: 'auto',
        },
        [breakpoints.down('sm')]: {
          fontSize: '13px',
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      },
      label: {
        fontWeight: 400,
        textTransform: 'capitalize',
        width: 'auto',
      },
      contained: {
        boxShadow: 'none',
        textTransform: 'capitalize',
      },
      containedPrimary: {
        color: '#FFFFFF',
        backgroundColor: '#0075CA',
        boxShadow: 'none',
        textTransform: 'capitalize',
        '&:hover': {
          backgroundColor: '#004A99',
          boxShadow: 'none',
        },
      },
      outlined: {
        color: '#000000',
        borderColor: 'rgba(0, 0, 0, 0.87)',
        backgroundColor: 'transparent',
        fontWeight: 400,
        '&:hover': {
          backgroundColor: '#EBEBEB',
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
      },
      outlinedPrimary: {
        color: '#000000',
        borderColor: 'rgba(0, 0, 0, 0.87)',
        backgroundColor: 'transparent',
        fontWeight: 400,
        '&:hover': {
          backgroundColor: '#EBEBEB',
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
      },
      text: {
        color: 'rgba(0, 0, 0, 0.54)',
        borderRadius: 0,
        backgroundColor: 'transparent',
        fontWeight: 400,
        '& .MuiButton-label': {
          fontWeight: 400,
        },
        '&:hover': {
          backgroundColor: 'rgba(0, 35, 75, 0.04)',
          color: 'rgba(0, 0, 0, 0.87)',
        },
      },
      textPrimary: {
        color: '#0075CA',
        borderRadius: 0,
        backgroundColor: 'transparent',
        fontWeight: 400,
        '& .MuiButton-label': {
          fontWeight: 400,
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: '#004A99',
        },
      },
      sizeLarge: {
        height: 48,
        minHeight: 48,
        padding: '12px 20px',
      },
      sizeSmall: {
        height: 32,
        minHeight: 32,
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
      startIcon: {
        marginRight: '8px',
        marginLeft: '0',
      },
      endIcon: {
        marginLeft: '8px',
        marginRight: '0',
      },
    },
  },
}

// theme overrides for family cup marketing UI
const familyCupMarketingTheme = {
  palette: {
    primary: {
      main: '#FF7500',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#49C5B1',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: '#000000',
      secondary: '#2C2D36',
    },
    background: {
      main: '#FAFAFA',
      light: '#FFFFFF',
      dark: '#49C5B1',
    },
    card: {
      border: '#EEE',
      borderHover: '#FF7500',
      divider: '#DBDCDD',
      distance: {
        background: '#EEE',
      },
      status: {
        border: '#49C5B1',
        text: '#2C2D36',
        background: {
          main: '#49C5B1',
          dark: '#2C2D36',
        },
      },
    },
  },
  shape: {
    borderRadius: '10px',
  },
  typography: {
    fontFamily: ['Inter', 'Inter', 'Inter', 'Helvetica', 'Arial', 'sans-serif'],
    button: {
      textTransform: 'capitalize',
      fontStyle: 'normal',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '21px',
    },
    h1: {
      fontFamily: 'Inter',
      fontSize: '54px',
      lineHeight: '70px',
      letterSpacing: 0,
    },
    h2: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '28px',
      lineHeight: '36px',
    },
    h3: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '20px',
      lineHeight: '26px',
    },
    h4: {
      fontFamily: 'Inter',
      fontSize: '20px',
      lineHeight: '34px',
    },
    h5: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '18px',
      lineHeight: '23px',
    },
    h6: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '21px',
    },
    body1: {
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '32px',
      letterSpacing: 0,
      textAlign: 'left',
    },
    body2: {
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '26px',
      letterSpacing: 0,
      textAlign: 'left',
    },
    caption: {
      fontSize: '14px',
      lineHeight: '24px',
    },
  },
  overrides: {
    MuiCssBaseline: {
      '@global': {
        '@font-face': plusJakarta,
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: '10px',
      },
    },
    MuiDivider: {
      vertical: {
        background: '#FFFFFF',
        opacity: 0.4,
      },
    },
    MuiListItemIcon: {
      root: {
        color: '#FFFFFF',
      },
    },
    MuiTableRow: {
      root: {
        background: '#FFFFFF',
        boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.03)',
        borderRadius: '10px',
        margin: '4px',
      },
    },
    MuiLink: {
      underlineHover: {
        '&:hover': {
          textDecoration: 'none',
          cursor: 'pointer',
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: '5px',
        padding: '16px 24px',
        cursor: 'pointer',
        textTransform: 'capitalize',
        '& .MuiButton-label': {
          textTransform: 'capitalize',
        },
        '&:hover': {
          backgroundColor: 'inherit',
        },
      },
      label: {
        textTransform: 'capitalize',
      },
      containedPrimary: {
        boxShadow: 'none',
        '&:hover': {
          boxShadow: 'none',
        },
      },
      outlinedSecondary: {
        padding: '19px 50px',
      },
      text: {
        color: '#FF7500',
        padding: '16px 24px',
      },
      sizeLarge: {
        padding: '18px 26px',
        fontSize: '1rem',
        lineHeight: '1.5',
      },
    },
    MuiButtonGroup: {
      root: {
        background: '#ffffff',
        borderRadius: '10px',
      },
      groupedTextPrimary: {
        textAlign: 'left',
        justifyContent: 'flex-start',
        '&:hover': {
          background: '#ffffff',
          opacity: 0.65,
          borderRadius: '10px',
        },
        '&:not(:last-child)': {
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
        '& span:last-child': {
          flex: 'auto',
          justifyContent: 'flex-end',
        },
      },
    },
    MuiChip: {
      root: {
        borderRadius: '6px',
      },
    },
  },
}

// theme overrides for junior league marketing UI
const juniorLeagueMarketingTheme = {
  palette: {
    primary: {
      main: '#0075CA',
      dark: '#004A99',
      light: '#5BA3FE',
      contrastText: '#FFFFFF',
      selected: 'rgba(0, 117, 202, 0.08)',
    },
    secondary: {
      main: '#FF7500',
      light: '#ffa641',
      dark: '#c44500',
      contrastText: '#FFFFFF',
    },
    text: {
      primary: '#000000',
      secondary: '#2C2D36',
    },
    background: {
      main: '#FAFAFA',
      light: '#FFFFFF',
      dark: '#0275CA',
    },
    chip: {
      background: {
        main: '#bddaff',
        disabled: '#f2f2f2',
      },
      text: {
        disabled: '#d9d9d9',
      },
    },
    card: {
      border: '#EEE',
      borderHover: '#FF7500',
      divider: '#DBDCDD',
      distance: {
        background: '#EEE',
      },
      status: {
        border: '#0275CA',
        text: '#2C2D36',
        background: {
          main: '#0275CA',
          dark: '#2C2D36',
        },
      },
    },
  },
  shape: {
    borderRadius: '10px',
  },
  typography: {
    fontFamily: ['Inter', 'Inter', 'Inter', 'Helvetica', 'Arial', 'sans-serif'],
    button: {
      textTransform: 'capitalize',
      fontStyle: 'normal',
      fontWeight: 700,
      fontSize: '16px',
      lineHeight: '21px',
    },
    h1: {
      fontFamily: 'Inter',
      fontSize: '54px',
      lineHeight: '70px',
      letterSpacing: 0,
    },
    h2: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '28px',
      lineHeight: '36px',
    },
    h3: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '20px',
      lineHeight: '26px',
    },
    h4: {
      fontFamily: 'Inter',
      fontSize: '20px',
      lineHeight: '34px',
    },
    h5: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 'bold',
      fontSize: '18px',
      lineHeight: '23px',
    },
    h6: {
      fontFamily: 'Inter',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '16px',
      lineHeight: '21px',
    },
    body1: {
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '32px',
      letterSpacing: 0,
      textAlign: 'left',
    },
    body2: {
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 'normal',
      lineHeight: '26px',
      letterSpacing: 0,
      textAlign: 'left',
    },
    caption: {
      fontSize: '14px',
      lineHeight: '24px',
    },
  },
  overrides: {
    MuiDrawer: {
      paper: {
        backgroundColor: '#0075CA',
        color: '#FFFFFF',
      },
    },
    MuiCssBaseline: {
      '@global': {
        '@font-face': plusJakarta,
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: '10px',
      },
    },
    MuiDivider: {
      vertical: {
        background: '#FFFFFF',
        opacity: 0.4,
      },
    },
    MuiListItemIcon: {
      root: {
        color: '#FFFFFF',
      },
    },
    MuiTableRow: {
      root: {
        background: '#FFFFFF',
        boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.03)',
        borderRadius: '10px',
        margin: '4px',
      },
    },
    MuiLink: {
      underlineHover: {
        '&:hover': {
          textDecoration: 'none',
          cursor: 'pointer',
        },
      },
    },
    MuiButtonBase: {
      root: {
        fontWeight: 400,
        '& .MuiButton-label': {
          fontWeight: 400,
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: '50px',
        boxShadow: 'none',
        textTransform: 'capitalize',
        fontWeight: 400,
        minWidth: 'auto',
        height: 40,
        minHeight: 40,
        padding: '8px 16px',
        fontSize: '0.875rem',
        lineHeight: 1.5,
        cursor: 'pointer',
        '& .MuiButton-label': {
          fontWeight: 400,
          textTransform: 'capitalize',
          width: 'auto',
        },
        [breakpoints.down('sm')]: {
          fontSize: '13px',
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      },
      label: {
        fontWeight: 400,
        textTransform: 'capitalize',
        width: 'auto',
      },
      contained: {
        boxShadow: 'none',
        textTransform: 'capitalize',
      },
      containedPrimary: {
        color: '#FFFFFF',
        backgroundColor: '#0075CA',
        boxShadow: 'none',
        textTransform: 'capitalize',
        '&:hover': {
          backgroundColor: '#004A99',
          boxShadow: 'none',
        },
      },
      containedSecondary: {
        color: '#FFFFFF',
        backgroundColor: '#FF7500',
        boxShadow: 'none',
        textTransform: 'capitalize',
        '&:hover': {
          backgroundColor: '#c44500',
          boxShadow: 'none',
        },
      },
      outlined: {
        color: '#000000',
        borderColor: 'rgba(0, 0, 0, 0.87)',
        backgroundColor: 'transparent',
        fontWeight: 400,
        '&:hover': {
          backgroundColor: '#EBEBEB',
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
      },
      outlinedPrimary: {
        color: '#000000',
        borderColor: 'rgba(0, 0, 0, 0.87)',
        backgroundColor: 'transparent',
        fontWeight: 400,
        '&:hover': {
          backgroundColor: '#EBEBEB',
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
      },
      outlinedSecondary: {
        padding: '8px 16px',
      },
      text: {
        color: '#FF7500',
        borderRadius: 0,
        backgroundColor: 'transparent',
        fontWeight: 400,
        padding: '8px 16px',
        '& .MuiButton-label': {
          fontWeight: 400,
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: '#c44500',
        },
      },
      textPrimary: {
        color: '#0075CA',
        borderRadius: 0,
        backgroundColor: 'transparent',
        fontWeight: 400,
        '& .MuiButton-label': {
          fontWeight: 400,
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: '#004A99',
        },
      },
      sizeLarge: {
        height: 48,
        minHeight: 48,
        padding: '12px 20px',
      },
      sizeSmall: {
        height: 32,
        minHeight: 32,
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
      startIcon: {
        marginRight: '8px',
        marginLeft: '0',
      },
      endIcon: {
        marginLeft: '8px',
        marginRight: '0',
      },
    },
    MuiButtonGroup: {
      root: {
        background: '#ffffff',
        borderRadius: '10px',
      },
      groupedTextPrimary: {
        textAlign: 'left',
        justifyContent: 'flex-start',
        '&:hover': {
          background: '#ffffff',
          opacity: 0.65,
          borderRadius: '10px',
        },
        '&:not(:last-child)': {
          borderColor: 'rgba(0, 0, 0, 0.08)',
        },
        '& span:last-child': {
          flex: 'auto',
          justifyContent: 'flex-end',
        },
      },
    },
    MuiChip: {
      root: {
        borderRadius: '6px',
      },
    },
  },
}

// theme overrides for PGA coach UI
const pgaCoachTheme = {
  palette: {
    primary: {
      main: '#00234B',
      light: '#043362',
      dark: '#001834',
      contrastText: '#FFFFFF',
      active: 'rgba(0, 35, 75, 0.24)',
      selected: 'rgba(0, 35, 75, 0.08)',
      border: 'rgba(0, 35, 75, 0.5)',
    },
    secondary: {
      main: '#AB9157',
      light: '#BBA778',
      dark: '#77653C',
      contrastText: '#FFFFFF',
    },
    text: {
      secondary: 'rgba(0, 0, 0, 0.54)',
    },
    blue: {
      badge: '#2380FF',
      main: '#0053FF',
    },
  },
  typography: {
    fontFamily: ['Inter', 'Inter', 'Inter', 'Helvetica', 'Arial', 'sans-serif'],
    button: {
      textTransform: 'capitalize',
      fontWeight: 400,
    },
    h4: {
      [breakpoints.down('sm')]: {
        fontSize: '22px',
      },
    },
  },
  props: {
    MuiTabs: {
      indicatorColor: 'primary',
      textColor: 'primary',
    },
    MuiSwitch: {
      color: 'primary',
    },
    MuiLinearProgress: {
      color: 'primary',
    },
    MuiCircularProgress: {
      color: 'primary',
    },
    MuiDialog: {
      PaperProps: {
        style: {
          borderRadius: 12,
          overflow: 'hidden',
        },
      },
    },
  },
  overrides: {
    MuiListItemIcon: {
      root: {
        color: 'rgba(0, 0, 0, 0.87)',
        minWidth: '2.25rem', // 36px at 16px font size
      },
    },
    MUIRichTextEditor: {
      toolbar: {
        borderBottom: '1px solid #E0E0E0',
        marginBottom: '10px',
      },
      editorContainer: {
        height: '100%',
      },
    },
    MuiButtonBase: {
      root: {
        fontWeight: 400,
        '& .MuiButton-label': {
          fontWeight: 400,
        },
      },
    },
    MuiButton: {
      root: {
        borderRadius: '50px',
        boxShadow: 'none',
        textTransform: 'capitalize',
        fontWeight: 400,
        minWidth: 'auto',
        height: 40,
        minHeight: 40,
        padding: '8px 16px',
        fontSize: '0.875rem',
        lineHeight: 1.5,
        '& .MuiButton-label': {
          fontWeight: 400,
          textTransform: 'capitalize',
          width: 'auto',
        },
        [breakpoints.down('sm')]: {
          fontSize: '13px',
          paddingLeft: '10px',
          paddingRight: '10px',
        },
      },
      label: {
        fontWeight: 400,
        textTransform: 'capitalize',
        width: 'auto',
      },
      contained: {
        boxShadow: 'none',
        textTransform: 'capitalize',
      },
      containedPrimary: {
        color: '#FFFFFF',
        backgroundColor: '#0053FF',
        boxShadow: 'none',
        textTransform: 'capitalize',
        '&:hover': {
          backgroundColor: '#0046D6',
          boxShadow: 'none',
        },
      },
      outlined: {
        color: '#000000',
        borderColor: 'rgba(0, 0, 0, 0.87)',
        backgroundColor: 'transparent',
        fontWeight: 400,
        '&:hover': {
          backgroundColor: '#EBEBEB',
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
      },
      outlinedPrimary: {
        color: '#000000',
        borderColor: 'rgba(0, 0, 0, 0.87)',
        backgroundColor: 'transparent',
        fontWeight: 400,
        '&:hover': {
          backgroundColor: '#EBEBEB',
          borderColor: 'rgba(0, 0, 0, 0.87)',
        },
      },
      text: {
        color: 'rgba(0, 0, 0, 0.54)',
        borderRadius: 0,
        backgroundColor: 'transparent',
        fontWeight: 400,
        '& .MuiButton-label': {
          fontWeight: 400,
        },
        '&:hover': {
          backgroundColor: 'rgba(0, 35, 75, 0.04)',
          color: 'rgba(0, 0, 0, 0.87)',
        },
      },
      textPrimary: {
        color: '#0053FF',
        borderRadius: 0,
        backgroundColor: 'transparent',
        fontWeight: 400,
        '& .MuiButton-label': {
          fontWeight: 400,
        },
        '&:hover': {
          backgroundColor: 'transparent',
          color: '#0046D6',
        },
      },
      sizeLarge: {
        height: 48,
        minHeight: 48,
        padding: '12px 20px',
      },
      sizeSmall: {
        height: 32,
        minHeight: 32,
        padding: '4px 12px',
        fontSize: '0.8125rem',
      },
      startIcon: {
        marginRight: '8px',
        marginLeft: '0',
      },
      endIcon: {
        marginLeft: '8px',
        marginRight: '0',
      },
    },
    MuiLinearProgress: {
      colorPrimary: {
        backgroundColor: 'rgba(0, 35, 75, 0.24)',
      },
      barColorPrimary: {
        backgroundColor: '#00234B',
      },
      colorSecondary: {
        backgroundColor: 'rgba(0, 35, 75, 0.24)',
      },
      barColorSecondary: {
        backgroundColor: '#00234B',
      },
    },
    MuiCircularProgress: {
      colorPrimary: {
        color: '#00234B',
      },
      colorSecondary: {
        color: '#00234B',
      },
    },
    MuiSelect: {
      root: {
        backgroundColor: '#ffffff',
      },
    },
    MuiToggleButton: {
      root: {
        border: '1px solid #043362',
        borderColor: '#043362',
        color: '#043362',
        width: '50%',
        '&$selected': {
          backgroundColor: '#00234B',
          color: 'white',
          '&:hover': {
            backgroundColor: '#00234B',
          },
        },
      },
    },
    MuiTabs: {
      root: {
        minHeight: 48,
        borderBottom: '1px solid #E0E0E0',
      },
      indicator: {
        display: 'none',
      },
      flexContainer: {
        gap: 8,
      },
    },
    MuiTab: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '1rem',
        minHeight: 48,
        minWidth: 'auto',
        maxWidth: 'none',
        padding: '12px 16px 0',
        color: 'rgba(0, 0, 0, 0.87)',
        opacity: 1,
        '&:hover': {
          color: 'rgba(0, 0, 0, 0.87)',
          backgroundColor: 'rgba(0, 35, 75, 0.04)',
        },
        '&:active': {
          backgroundColor: 'rgba(0, 83, 255, 0.08)',
        },
        '&$selected': {
          color: '#0053FF',
          fontWeight: 500,
          backgroundColor: 'transparent',
        },
        '&$selected:focus': {
          backgroundColor: 'transparent',
        },
        '&$selected $wrapper': {
          color: '#0053FF',
        },
        '&$selected:hover': {
          color: '#0053FF',
          backgroundColor: 'rgba(0, 35, 75, 0.04)',
        },
        '&$selected:active': {
          backgroundColor: 'rgba(0, 83, 255, 0.08)',
        },
        '&$selected $wrapper::after': {
          backgroundColor: '#0053FF',
        },
      },
      wrapper: {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 'fit-content',
        maxWidth: '100%',
        '&::after': {
          content: '""',
          display: 'block',
          width: '100%',
          height: 3,
          marginTop: 8,
          borderRadius: 3,
          backgroundColor: 'transparent',
        },
      },
      textColorPrimary: {
        color: 'rgba(0, 0, 0, 0.87)',
        opacity: 1,
        '&$selected': {
          color: '#0053FF',
          opacity: 1,
        },
      },
    },
    MuiAppBar: {
      root: {
        borderRadius: 0,
      },
      colorPrimary: {
        borderRadius: 0,
      },
      colorDefault: {
        borderRadius: 0,
      },
    },
    MuiPaper: {
      root: {
        borderRadius: 12,
      },
      rounded: {
        borderRadius: 12,
      },
      outlined: {
        borderRadius: 12,
      },
      elevation0: {
        borderRadius: 12,
      },
      elevation1: {
        borderRadius: 12,
      },
      elevation2: {
        borderRadius: 12,
      },
      elevation24: {
        borderRadius: 12,
      },
    },
    MuiCard: {
      root: {
        borderRadius: 12,
        overflow: 'hidden',
      },
    },
    MuiDialog: {
      paper: {
        borderRadius: 12,
        overflow: 'hidden',
      },
      paperScrollPaper: {
        borderRadius: 12,
      },
    },
    MuiPopover: {
      paper: {
        borderRadius: 12,
      },
    },
    MuiMenu: {
      paper: {
        borderRadius: 12,
      },
    },
    MuiSnackbarContent: {
      root: {
        borderRadius: 12,
      },
    },
    MuiChip: {
      root: {
        borderRadius: 12,
      },
    },
    ...outlinedInputThemeOverrides,
  },
}

const offeringsRefreshPalette = {
  primary: {
    lightest: '#3268A7',
    light: '#043362',
    main: '#00234B',
    dark: '#001123',
    contrastText: '#FFFFFF',
  },
  secondary: {
    light: '#DBD2C1',
    main: '#AB9157',
    dark: '#836F50',
    neutral: '#FAF6EF',
    contrastText: '#FFFFFF',
  },
  ryderCup: {
    usa: '#C81414',
    intl: '#003C82',
    neutral: '#828282',
  },
  accent: {
    main: '#FFBE00',
    red: '#D32F2F',
  },
  grey: {
    darkest: '#090909',
    darker: '#555555',
    dark: '#6A6A6A',
    light: '#D8D8D8',
    main: '#9E9E9E',
  },
  blue: {
    main: '#3268A7',
    light: '#EFF5FB',
    light25: '#F5F7FA',
    light100: '#EBEFF5',
    dark: '#001529',
    medium: '#EBEFF5',
  },
  green: {
    light: '#EDF4EA',
    dark: '#002C21',
  },
  gold: {
    main: '#E6BC5A',
  },
  opaque: {
    white: '#FFFFFFF2',
    blue: '#00234BE6',
  },
  offwhite: {
    main: '#F7F7F7',
    secondary: '#EBEBEB',
  },
  background: {
    default: '#F7F7F7',
    gradient: `
      linear-gradient(180deg,
        rgba(0, 0, 0, 0.25) 0%,
        rgba(0, 0, 0, 0.2) 10%,
        rgba(0, 0, 0, 0.15) 30%,
        rgba(0, 0, 0, 0.5) 50%,
        rgba(0, 0, 0, 0.6) 100%
      )`,
  },
  text: {
    primary: '#000000',
  },
  shadow: {
    cardBoxShadow: '0px 4px 36px rgba(0, 10, 22, 0.13)',
  },
}

const offeringsRefreshHeadlineFont = ['Source Serif Pro', 'serif'].join(',')
const offeringsRefreshDefaultFont = ['Nunito Sans', 'sans-serif'].join(',')
const offeringsRefreshEyebrowFont = ['Roboto Condensed', 'sans-serif'].join(',')

const offeringsRefreshTheme = {
  palette: offeringsRefreshPalette,
  shape: {
    borderRadius: 0,
  },
  typography: {
    fontFamily: offeringsRefreshDefaultFont,
    fontSize: 14,
    h1: {
      fontSize: '60px',
      fontFamily: offeringsRefreshHeadlineFont,
      fontWeight: headlineWeight,
      lineHeight: 1,
      color: offeringsRefreshPalette.primary.main,
    },
    h2: {
      fontSize: '50px',
      fontFamily: offeringsRefreshHeadlineFont,
      fontWeight: headlineWeight,
      lineHeight: 1.2,
      color: offeringsRefreshPalette.primary.main,
      [breakpoints.only('xs')]: {
        fontSize: '40px',
        lineHeight: 1.12,
      },
    },
    h3: {
      fontSize: '40px',
      fontFamily: offeringsRefreshHeadlineFont,
      fontWeight: headlineWeight,
      lineHeight: 1.3,
      color: offeringsRefreshPalette.primary.main,
      [breakpoints.only('xs')]: {
        fontSize: '32px',
      },
    },
    h4: {
      fontSize: '32px',
      fontFamily: offeringsRefreshHeadlineFont,
      fontWeight: headlineWeight,
      lineHeight: 1.25,
      color: offeringsRefreshPalette.primary.main,
      [breakpoints.only('xs')]: {
        fontSize: '24px',
      },
    },
    h5: {
      fontSize: '28px',
      fontFamily: offeringsRefreshHeadlineFont,
      fontWeight: headlineWeight,
      lineHeight: 1.16,
      color: offeringsRefreshPalette.primary.main,
    },
    h6: {
      fontSize: '20px',
      fontFamily: offeringsRefreshHeadlineFont,
      fontWeight: headlineWeight,
      lineHeight: 1.3,
      color: offeringsRefreshPalette.primary.main,
    },
    body1: {
      lineHeight: 1.75,
      fontSize: '16px',
      fontWeight: bodyWeight,
    },
    body2: {
      lineHeight: 1.75,
      fontSize: '14px',
      fontWeight: bodyWeight,
    },
    subtitle1: {
      // Subtitle Large
      fontSize: '16px',
      letterSpacing: 0.1,
      lineHeight: '20px',
      fontWeight: utilityWeight,
    },
    subtitle2: {
      // Subtitle Small
      fontSize: '14px',
      letterSpacing: 0.1,
      lineHeight: '20px',
      fontWeight: utilityWeight,
    },
    subtitleXl: {
      fontSize: '18px',
      letterSpacing: 0.1,
      lineHeight: '20px',
      fontWeight: utilityWeight,
    },
    button: {
      fontSize: '14px',
      letterSpacing: 0.1,
    },
    caption: {
      fontSize: '12px',
      letterSpacing: 0.4,
      lineHeight: '16px',
      fontWeight: '600',
    },
    overline: {
      fontSize: '14px',
      letterSpacing: 1.5,
      fontWeight: 'bold',
      fontFamily: offeringsRefreshEyebrowFont,
      display: 'block',
    },
    cta: {
      fontSize: '16px',
      lineHeight: '20px',
    },
    formLabels: {
      fontSize: '14px',
      lineHeight: '25px',
      fontWeight: utilityWeight,
    },
    formInputs: {
      fontSize: '16px',
      lineHeight: '19px',
      fontWeight: utilityWeight,
    },
    formTags: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: utilityWeight,
    },
    cardFilters: {
      fontSize: '12px',
      lineHeight: '16px',
    },
    cardItemCategory: {
      fontFamily: offeringsRefreshEyebrowFont,
      fontSize: '12px',
      lineHeight: '16px',
      textTransform: 'uppercase',
    },
    navigation: {
      fontSize: '16px',
      fontFamily: offeringsRefreshHeadlineFont,
      fontWeight: headlineWeight,
      lineHeight: 1.253125,
    },
  },
  props: {
    MuiCard: {
      elevation: 0,
    },
    MuiButtonBase: {
      disableRipple: true,
    },
  },
  overrides: {
    MuiButton: {
      root: {
        boxShadow: 'none',
        height: 48,
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '.625rem 1.5625rem',
        fontSize: '1rem',
        lineHeight: 1.75,
        fontFamily: 'Nunito Sans',
        textTransform: 'capitalize',
        fontWeight: 700,
        borderRadius: '50px',
        textDecoration: 'underline',
        textUnderlineOffset: '2px',
        textDecorationColor: 'transparent',
        transition:
          'text-decoration-color 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,border 250ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        '&:hover': {
          textDecoration: 'underline',
        },
      },
      contained: {
        boxShadow: 'none',
      },
      outlined: {
        padding: '0 72px',
      },
      fullWidth: {
        padding: '0 1rem',
      },
      startIcon: {
        marginRight: '5px',
        marginLeft: '0',
      },
      endIcon: {
        marginLeft: '5px',
        marginRight: '0',
      },
      containedPrimary: {
        color: offeringsRefreshPalette.primary.contrastText,
        backgroundColor: offeringsRefreshPalette.primary.lightest,
        '&:hover': {
          backgroundColor: offeringsRefreshPalette.primary.main,
          color: offeringsRefreshPalette.primary.contrastText,
          textDecorationColor: offeringsRefreshPalette.primary.contrastText,
        },
      },
      containedSecondary: {
        color: offeringsRefreshPalette.text.primary,
        backgroundColor: 'white',
        '&:hover': {
          backgroundColor: offeringsRefreshPalette.gold.main,
          textDecorationColor: offeringsRefreshPalette.text.primary,
        },
      },
      sizeSmall: {
        height: 40,
        fontSize: '.875rem',
        padding: '.24rem 1.5625rem',
      },
      containedSizeSmall: {
        padding: '.24rem 1.5625rem',
      },
      textSizeSmall: {
        padding: '.24rem 1.5625rem',
      },
      text: {
        color: offeringsRefreshPalette.blue.main,
        padding: '.625rem 1.5625rem',
        borderRadius: 0,
        '&:hover': {
          color: offeringsRefreshPalette.primary.dark,
          backgroundColor: 'transparent',
        },
      },
      textPrimary: {
        padding: '.625rem 1.5625rem',
        color: offeringsRefreshPalette.blue.main,
        fontFamily: 'Nunito Sans',
        '&:hover': {
          color: offeringsRefreshPalette.primary.main,
          backgroundColor: 'transparent',
        },
      },
      outlinedPrimary: {
        padding: '.625rem 1.5625rem',
        color: offeringsRefreshPalette.primary.light,
        borderColor: offeringsRefreshPalette.primary.light,
        '&:hover': {
          backgroundColor: offeringsRefreshPalette.offwhite.secondary,
          color: offeringsRefreshPalette.primary.dark,
          borderColor: offeringsRefreshPalette.primary.light,
          textDecorationColor: 'currentColor',
        },
      },
    },
    MuiTableCell: {
      root: {
        padding: 8,
      },
    },
    MuiBreadcrumbs: {
      separator: {
        color: '#9e9e9e',
      },
    },
    MuiAvatar: {
      root: {
        height: 110,
        width: 110,
        fontFamily: offeringsRefreshHeadlineFont,
      },
    },
    MuiPickersDay: {
      current: {
        color: '#000000',
        '& .MuiIconButton-label p': {
          color: '#000000',
          fontWeight: '600',
        },
        '&.MuiPickersDay-dayDisabled .MuiIconButton-label p': {
          color: 'inherit',
        },
        '&.MuiPickersDay-daySelected .MuiIconButton-label p': {
          color: '#FFFFFF',
        },
      },
      daySelected: {
        backgroundColor: offeringsRefreshPalette.primary.light,
        '&:hover': {
          backgroundColor: offeringsRefreshPalette.primary.light,
        },
      },
    },
    MuiPaginationItem: {
      root: {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        fontFamily: offeringsRefreshHeadlineFont,
        fontSize: '18px',
        margin: '0px',
        color: offeringsRefreshPalette.grey.dark,
      },
      page: {
        '&.Mui-selected': {
          backgroundColor: offeringsRefreshPalette.primary.main,
          color: offeringsRefreshPalette.primary.contrastText,
        },
      },
    },
    MuiSvgIcon: {
      colorDisabled: {
        color: pgaCoachTheme.palette.primary.selected,
      },
    },
    ...outlinedInputThemeOverrides,
  },
}

const pgaHopeRefreshTheme = {
  palette: pgaCoachTheme,
  shape: {
    borderRadius: 0,
  },
  typography: {
    fontFamily: defaultFont,
    fontSize: 14,
    h4: {
      fontSize: '32px',
      fontFamily: headlineFont,
      fontWeight: headlineWeight,
      letterSpacing: -1,
      lineHeight: 1.25,
      [breakpoints.down('sm')]: {
        fontSize: '22px',
      },
    },
    h5: {
      fontSize: '24px',
      fontFamily: headlineFont,
      fontWeight: headlineWeight,
      letterSpacing: 0,
      lineHeight: 1.16,
      [breakpoints.down('sm')]: {
        fontSize: '20px',
      },
    },
    body1: {
      lineHeight: 1.75,
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: defaultFont,
      [breakpoints.down('sm')]: {
        fontSize: '14px',
        lineHeight: '20px',
      },
    },
    formLabels: {
      fontSize: '14px',
      lineHeight: '25px',
      fontWeight: utilityWeight,
      fontFamily: 'Inter',
    },
    formInputs: {
      fontSize: '16px',
      lineHeight: '19px',
      fontWeight: utilityWeight,
      fontFamily: 'Inter',
    },
    formTags: {
      fontSize: '12px',
      lineHeight: '16px',
      fontWeight: utilityWeight,
      fontFamily: formFont,
    },
  },
  overrides: {
    MuiInputBase: {
      root: {
        fontSize: '16px',
        fontFamily: formFont,
        fontWeight: '400',
      },
    },
    MuiFormLabel: {
      root: {
        fontFamily: formFont,
        fontWeight: '400',
      },
    },
    MuiButtonBase: {
      root: {
        fontFamily: formFont,
        fontWeight: '400',
      },
    },
  },
}

// theme overrides for Intercom Dialog Work-Arounds
const intercomOverrideTheme = {
  props: {
    MuiDialog: {
      disableAutoFocus: true,
      disableEnforceFocus: true,
    },
  },
}

const mergeThemeSections = (base = {}, extension = {}) => {
  const merged = { ...base }

  Object.keys(extension).forEach(key => {
    const extensionValue = extension[key]
    const baseValue = base[key]

    if (
      extensionValue &&
      typeof extensionValue === 'object' &&
      !Array.isArray(extensionValue)
    ) {
      merged[key] = {
        ...(baseValue || {}),
        ...extensionValue,
      }
      return
    }

    merged[key] = extensionValue
  })

  return merged
}

const extendTheme = extensions => theme => {
  return createTheme({
    ...theme,
    shape: {
      ...theme.shape,
      ...(extensions.shape || {}),
    },
    palette: {
      ...theme.palette,
      ...(extensions.palette || {}),
    },
    typography: mergeThemeSections(theme.typography, extensions.typography),
    props: mergeThemeSections(theme.props, extensions.props),
    overrides: mergeThemeSections(theme.overrides, extensions.overrides),
  })
}

const themes = {
  common: commonTheme,
  familyCup: familyCupTheme,
  familyCupMarketing: familyCupMarketingTheme,
  pgaCoach: pgaCoachTheme,
  offeringsRefresh: offeringsRefreshTheme,
  pgaHope: pgaHopeRefreshTheme,
  intercom: intercomOverrideTheme,
  juniorLeague: juniorLeagueTheme,
  juniorLeagueMarketing: juniorLeagueMarketingTheme,
}

export default themes
export { extendTheme }
